import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useCombobox } from 'downshift';

import Input from './Input';

/**
 * Prevents users from defining possibly invalid regular expressions by effectively
 * blocking the use of RegExp lexical tokens
 *
 * @param string
 * @returns {*}
 */

function escapeRegExp(string = '') {
  return string.replace(/[$()*+.?[\\\]^{|}-]/g, '\\$&');
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OptionList = styled.ul`
  display: ${props => (props.$visible ? 'block' : 'none')};
  position: absolute;
  top: calc(100% + 6px);
  background-color: var(--dropdown-bg);
  border-color: var(--dropdown-inactive);
  border-radius: 6px;
  box-shadow: var(--dropdown-menu-shadow);
  cursor: pointer;
  list-style-type: none;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  width: 100%;
  z-index: 1;
`;

const Option = styled.li`
  border-radius: 6px;
  color: var(--dropdown-text);
  padding: 8px;
  user-select: none;

  ${props =>
    props.$isFocused &&
    css`
      background-color: var(--dropdown-option-bg);
      color: var(--dropdown-active);
    `},
`;

const Mark = styled.mark`
  font-weight: var(--bold);
  color: var(--dropdown-text);
  background-color: unset;
`;

const Heading = styled.div`
  color: var(--dark-resting);
  font-size: var(--xx-small);
  font-weight: var(--normal);
  padding: 4px 8px;
  text-transform: uppercase;
  user-select: none;
`;

const getPattern = value => new RegExp(`(${escapeRegExp(value)})`, 'i');

function DataList({
  value = '',
  suggestions = [],
  onSelectedItemChange,
  ...unhandledProps
}) {
  const [matches, setMatches] = useState([]);

  const handleInputValueChange = ({ inputValue }) => {
    const pattern = getPattern(inputValue);
    setMatches([
      ...new Set(suggestions.filter(suggestion => pattern.test(suggestion))),
    ]);
  };

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    inputValue,
    isOpen,
    openMenu,
  } = useCombobox({
    items: matches,
    initialInputValue: value,
    onInputValueChange: handleInputValueChange,
    onSelectedItemChange,
  });

  const pattern = getPattern(inputValue);
  const [firstSuggestion] = suggestions;

  const showMenu = isOpen && matches.length > 0;

  const handleFocus = () => {
    if (!isOpen) {
      openMenu();
    }
  };

  return (
    <Wrapper {...getComboboxProps(unhandledProps)}>
      <Input
        type="text"
        {...getInputProps({
          placeholder: firstSuggestion && `e.g. ${firstSuggestion}`,
          onFocus: handleFocus,
        })}
      />
      <OptionList $visible={showMenu} {...getMenuProps()}>
        <Heading role="heading">Example values</Heading>
        {matches.map((match, index) => (
          <Option
            $isFocused={highlightedIndex === index}
            key={match}
            {...getItemProps({ item: match, index })}
          >
            {match.split(pattern).map((part, partialIndex) =>
              inputValue && pattern.test(part) ? (
                // eslint-disable-next-line react/no-array-index-key
                <Mark key={partialIndex}>{part}</Mark>
              ) : (
                part
              )
            )}
          </Option>
        ))}
      </OptionList>
    </Wrapper>
  );
}

DataList.propTypes = {
  value: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  onSelectedItemChange: PropTypes.func,
};

export default styled(DataList)``;
